import type { TagCategory } from '../data/api.js';

const EXCLUDED_TIMELINE_FEATURES = ['Hook Scene', 'End Card', 'Duration'];
const EXCLUDED_TIMELINE_TAGS = ['Not Included'];

export interface TagFilter {
  features: string[];
  tags: string[];
}

export function filterTagCategories(categories: TagCategory[], filter: TagFilter): TagCategory[] {
  return categories
    .map((category) => ({
      ...category,
      features: category.features
        .filter(
          (feature) => filter.features.length === 0 || filter.features.includes(feature.feature),
        )
        .map((feature) => ({
          ...feature,
          tags: feature.tags.filter(
            (tag) => filter.tags.length === 0 || filter.tags.includes(tag.tag),
          ),
        }))
        .filter((feature) => feature.tags.length > 0),
    }))
    .filter((category) => category.features.length > 0);
}

function belongsOnTimeline(feature: string, tag: { tag: string; spans: unknown[] }): boolean {
  return (
    tag.spans.length > 0 &&
    !EXCLUDED_TIMELINE_FEATURES.some((excluded) => feature.includes(excluded)) &&
    !EXCLUDED_TIMELINE_TAGS.some((excluded) => tag.tag.includes(excluded))
  );
}

export function timelineCategories(categories: TagCategory[]): TagCategory[] {
  return categories
    .map((category) => ({
      ...category,
      features: category.features
        .map((feature) => ({
          ...feature,
          tags: feature.tags.filter((tag) => belongsOnTimeline(feature.feature, tag)),
        }))
        .filter((feature) => feature.tags.length > 0),
    }))
    .filter((category) => category.features.length > 0);
}

export function latestSpanEnd(categories: TagCategory[]): number {
  let latest = 0;
  for (const category of categories)
    for (const feature of category.features)
      for (const tag of feature.tags)
        for (const span of tag.spans) latest = Math.max(latest, span.endSec);
  return latest;
}

export function countTags(categories: TagCategory[]): number {
  return categories.reduce(
    (total, category) =>
      total + category.features.reduce((sum, feature) => sum + feature.tags.length, 0),
    0,
  );
}

export function featureOptions(categories: TagCategory[]): string[] {
  const names = new Set<string>();
  for (const category of categories) {
    for (const feature of category.features) names.add(feature.feature);
  }
  return [...names].sort();
}

export function tagOptions(categories: TagCategory[], features: string[]): string[] {
  const names = new Set<string>();
  for (const category of categories) {
    for (const featureEntry of category.features) {
      if (features.length > 0 && !features.includes(featureEntry.feature)) continue;
      for (const tag of featureEntry.tags) names.add(tag.tag);
    }
  }
  return [...names].sort();
}
